import { isOfflineMode } from "@/helpers/offlineMode";
import {
  DealerContactDetail,
  DealerDeal,
  DealerNote,
  DealerOverview,
  DealerRate,
  DealerSearchResponse,
  DealerStockFeedItem,
} from "@/types/dealer";
import apiClient from "./apiClient";
import {
  mockDealerContacts,
  mockDealerDeals,
  mockDealerNotes,
  mockDealerOverview,
  mockDealerRates,
  mockDealerSearchResult,
  mockDealerStockFeed,
} from "./mockData";

const getSearchType = (filter: string): string => {
  if (/^\d+$/.test(filter)) return "ID";
  if (/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(filter)) return "Postcode";
  return "Name";
};

const searchDealers = async (filter: string, abortSignal?: AbortSignal): Promise<DealerSearchResponse> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));

    const normalizedFilter = filter.trim().toLowerCase();
    const isMatch = [
      mockDealerSearchResult.dealerId.toString(),
      mockDealerSearchResult.dealerName,
      mockDealerSearchResult.postcode ?? "",
    ].some((value) => value.toLowerCase().includes(normalizedFilter));
    const searchResults = isMatch ? [mockDealerSearchResult] : [];

    return {
      searchType: getSearchType(filter.trim()),
      searchCount: searchResults.length,
      searchResults,
    };
  }

  const response = await apiClient.get<DealerSearchResponse>("/dealer/search", {
    params: { filter },
    signal: abortSignal,
  });
  return response.data;
};

const getDealerOverview = async (dealerId: number, abortSignal?: AbortSignal): Promise<DealerOverview> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    if (dealerId !== mockDealerOverview.id) throw new Error("Dealer not found.");
    return mockDealerOverview;
  }

  const response = await apiClient.get<DealerOverview>(`/dealer/${dealerId}/overview`, {
    signal: abortSignal,
  });
  return response.data;
};

const getDealerTabData = async <T>(
  dealerId: number,
  path: string,
  mock: T[],
  abortSignal?: AbortSignal,
  params?: Record<string, unknown>,
): Promise<T[]> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return dealerId === mockDealerOverview.id ? mock : [];
  }

  const response = await apiClient.get<T[]>(`/dealer/${dealerId}/${path}`, { params, signal: abortSignal });
  return response.data;
};

const getDealerNotes = (dealerId: number, abortSignal?: AbortSignal): Promise<DealerNote[]> =>
  getDealerTabData(dealerId, "notes", mockDealerNotes, abortSignal, { dealerId });

const getDealerContacts = (dealerId: number, abortSignal?: AbortSignal): Promise<DealerContactDetail[]> =>
  getDealerTabData(dealerId, "contacts", mockDealerContacts, abortSignal);

const getDealerDeals = (dealerId: number, abortSignal?: AbortSignal): Promise<DealerDeal[]> =>
  getDealerTabData(dealerId, "deals", mockDealerDeals, abortSignal);

const getDealerRates = (dealerId: number, abortSignal?: AbortSignal): Promise<DealerRate[]> =>
  getDealerTabData(dealerId, "rates", mockDealerRates, abortSignal);

const getDealerStockFeed = (dealerId: number, abortSignal?: AbortSignal): Promise<DealerStockFeedItem[]> =>
  getDealerTabData(dealerId, "stockfeed", mockDealerStockFeed, abortSignal);

export const dealerService = {
  searchDealers,
  getDealerOverview,
  getDealerNotes,
  getDealerContacts,
  getDealerDeals,
  getDealerRates,
  getDealerStockFeed,
};